/**
 * Cart reducer helpers — the single chokepoint for cart membership.
 *
 * A cart line is identified by the server `sessionId` it pays for, not by the
 * client-minted `id`. The server dedupes checkout by sessionId, so if the cart
 * held two lines for one session the cart / `/pay` header showed a higher total
 * than Stripe charged (audit 2026-10-01 H-3: $14.96 shown vs $10.97 charged).
 */
export interface CartLine {
  id: string;
  sessionId: string;
  priceAmount: number;
}

/** Return the cart with `item` appended, or the same cart if its session is already in it. */
export function addCartItem<T extends CartLine>(cart: T[], item: T): T[] {
  if (cart.some((i) => i.sessionId === item.sessionId)) return cart;
  return [...cart, item];
}

/** Collapse any duplicate-session lines (e.g. carts persisted before the dedupe fix). */
export function dedupeCart<T extends CartLine>(cart: T[]): T[] {
  return cart.reduce<T[]>((acc, item) => addCartItem(acc, item), []);
}

export function cartTotal(cart: CartLine[]): number {
  return Math.round(cart.reduce((sum, i) => sum + (i.priceAmount || 0), 0) * 100) / 100;
}
